import React from "react";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import * as r from "../../js/constants/routes";
import { SelectURLGrid } from "../Widgets/Custom Inputs/Select";
import { requiredCell } from "../../js/utils/inputValidations";
import DataGridComponent from "../Widgets/DataGridComponent";
import { t } from "react-i18nify";
import { useEditionRightsPerUserStatus } from "../../js/utils/customHooks";
import { filterValidUser } from "./cst_DataGrid";
import { ListItem, ListItemText } from "@mui/material";
import { DateOnlyPickerGrid } from "../Widgets/Custom Inputs/CustomInputs";
import { toastr } from "react-redux-toastr";

const ViewerGrid = ({
    label = t("grid.title.Viewers"),
    baseURL,
}) => {
    const apiRef = useGridApiRef();
    const cellFocusField = "OTUser_select";
    const editingRights = useEditionRightsPerUserStatus([
        "BranchManager",
        "Viewer",
    ]);

    const gridSpecificColumns = [
        {
            field: "OTUser_IGG",
            headerName: "IGG",
            sortable: false,
            flex: 0.5,
            valueGetter: params => params.row.OTUser_select?.IGG,
        },
        {
            field: "OTUser_select",
            headerName: t("field.User"),
            sortable: false,
            flex: 1,
            editable: true,
            valueFormatter: params => params.value?.Name,
            renderEditCell: params => (
                <SelectURLGrid
                    fieldName={"OTUserID"}
                    selectRoute={"SelectDetail"}
                    URL={r.oTUser}
                    paramURL={filterValidUser}
                    getOptionLabel={option => option.Name ?? option.IGG}
                    filterOptions={options => options}
                    renderOption={(props, option) => (
                        <ListItem {...props} key={option.ObjectID}>
                            <ListItemText
                                sx={{
                                    margin: 0,
                                    padding: 0,
                                }}
                                inset
                                primary={option.Name}
                                secondary={option.IGG}
                            />
                        </ListItem>
                    )}
                    {...params}
                />
            ),
            preProcessEditCellProps: params => {
                const hasError = requiredCell(
                    params,
                    apiRef,
                    "User",
                    "OTUser_select"
                );
                return { ...params.props, error: hasError };
            },
            valueSetter: params => ({
                ...params.row,
                EMail: params.value.EMail,
            }),
        },
        {
            field: "OTUserID",
            editable: false,
        },
        {
            field: "EMail",
            headerName: "Email",
            sortable: false,
            flex: 0.5,
            valueGetter: params => params.row.OTUser_select?.EMail,
        },
        {
            field: "OTEntity_select",
            headerName: t("field.Entity"),
            sortable: false,
            flex: 1,
            width: 200,
            editable: true,
            valueFormatter: params => params.value?.Name,
            renderEditCell: params => (
                <SelectURLGrid
                    fieldName={"OTEntityID"}
                    URL={r.oTEntity}
                    {...params}
                />
            ),
            preProcessEditCellProps: params => {
                const hasError = requiredCell(
                    params,
                    apiRef,
                    "Entity",
                    "OTEntity_select"
                );
                return { ...params.props, error: hasError };
            },
        },
        {
            field: "OTEntityID",
            valueGetter: params => params.row?.OTEntity_select?.ObjectID,
        },
        {
            field: "DeactivationDate",
            headerName: t("field.DeactivationDate"),
            sortable: true,
            flex: 1,
            editable: true,
            type: "date",
            valueFormatter: params => {
                if (!params.value) return "-";
                const d = params.value instanceof Date ? params.value : new Date(params.value);
                return isNaN(d) ? "-" : d.toLocaleDateString(navigator.language);
            },
            preProcessEditCellProps: params => {
                const val = params.props.value;
                if (!val) return { ...params.props, error: true };
                let date;
                if (typeof val === "string" && val.includes("/")) {
                    const [d, m, y] = val.split("/");
                    date = new Date(`${y}-${m}-${d}`);
                } else {
                    date = new Date(val);
                }
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return { ...params.props, error: isNaN(date.getTime()) || date <= today };
            },
            renderEditCell: params => (
                <DateOnlyPickerGrid
                    fieldName={params.field}
                    error={params.error}
                    {...params}
                />
            ),
        },
    ];

    return (
        <DataGridComponent
            apiRef={apiRef}
            baseURL={baseURL}
            columnVisibilityModel={{
                "OTUserID": false,
                "OTEntityID": false
            }}
            cellFocusField={cellFocusField}
            label={label}
            gridSpecificColumns={gridSpecificColumns}
            isEditDisabled={true}
            initialState={{
                sorting: {
                    sortModel: [{ field: "OTUserID", sort: "asc" }],
                },
            }}
            editingRights={editingRights}
            beforeCommitRow={row => {
                if (row.DeactivationDate && typeof row.DeactivationDate === "string" && row.DeactivationDate.includes("/")) {
                    const [d, m, y] = row.DeactivationDate.split("/");
                    row = { ...row, DeactivationDate: `${y}-${m}-${d}T00:00:00.000Z` };
                } else if (!(row.DeactivationDate instanceof Date) && row.DeactivationDate !== null) {
                    row = { ...row, DeactivationDate: null };
                }
                return row;
            }}
            onSaveError={() => toastr.error(`${t("field.DeactivationDate")} — ${t("input.validation.dateMustBeInFuture")}`)}
        />
    );
};

export default ViewerGrid;
